import { useState, useEffect, useCallback } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../config/firebase";
import {
  FaEuroSign,
  FaCalendarAlt,
  FaUsers,
  FaSignOutAlt,
  FaSyncAlt,
  FaHandshake,
} from "react-icons/fa";

const COMMISSION_RATE = 0.1; // 10%
const SESSION_KEY = "sp_partner";

/* ── Status badge ───────────────────────────────────────────── */
const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800 ring-1 ring-amber-200",
  confirmed: "bg-blue-100  text-blue-800  ring-1 ring-blue-200",
  completed: "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200",
  cancelled: "bg-red-100   text-red-800   ring-1 ring-red-200",
};

function fmt(n) {
  return Number(n || 0).toFixed(2);
}

/* ── Login screen ───────────────────────────────────────────── */
function LoginScreen({ onLogin }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const handleSubmit = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setError(false);
    try {
      const { collection, query, where, getDocs } =
        await import("firebase/firestore");
      const { db } = await import("../config/firebase");
      const snap = await getDocs(
        query(collection(db, "partners"), where("code", "==", code.trim())),
      );
      if (snap.empty) throw new Error("Invalid code");
      const partner = snap.docs[0].data();
      if (partner.active === false) throw new Error("Inactive");
      const info = { code: partner.code, name: partner.name || "Partner" };
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(info));
      onLogin(info);
    } catch (err) {
      console.error("Partner login error:", err?.code, err?.message, err);
      setError(true);
      setTimeout(() => setError(false), 2500);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-sky-900 px-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="bg-gradient-to-r from-sky-600 to-cyan-500 px-8 py-8 text-center">
          <FaHandshake className="text-white text-4xl mx-auto mb-3" />
          <h1 className="text-white text-2xl font-bold">Partner Portal</h1>
          <p className="text-sky-100 text-sm mt-1">SeaPulse Albania</p>
        </div>
        <div className="px-8 py-8 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">
              Partner Code
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              placeholder="Enter your partner code"
              className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
            />
          </div>
          {error && (
            <p className="text-red-500 text-sm text-center">
              Invalid partner code.
            </p>
          )}
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full py-3 bg-gradient-to-r from-sky-600 to-cyan-500 text-white rounded-xl font-semibold text-sm hover:opacity-90 transition disabled:opacity-50"
          >
            {loading ? "Verifying…" : "Access Dashboard"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Dashboard ──────────────────────────────────────────────── */
function Dashboard({ partner, onLogout }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, "bookings"),
        where("partnerCode", "==", partner.code),
      );
      const snap = await getDocs(q);
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => {
        const at = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
        const bt = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
        return bt - at;
      });
      setBookings(rows);
    } catch (err) {
      console.error("Partner load error:", err);
    } finally {
      setLoading(false);
    }
  }, [partner.code]);

  useEffect(() => {
    load();
  }, [load]);

  /* Filtered list */
  const visible =
    filter === "all" ? bookings : bookings.filter((b) => b.status === filter);

  /* Stats */
  const active = bookings.filter((b) => b.status !== "cancelled");
  const totalRev = active.reduce((s, b) => s + Number(b.totalPrice || 0), 0);
  const earned = active.reduce(
    (s, b) => s + Number(b.totalPrice || 0) * COMMISSION_RATE,
    0,
  );
  const pending = active.filter((b) => b.status === "pending").length;
  const confirmed = active.filter(
    (b) => b.status === "confirmed" || b.status === "completed",
  ).length;

  const formatDate = (ts) => {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-sky-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-sky-700 to-cyan-600 shadow">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-white text-lg font-bold">Partner Dashboard</h1>
            <p className="text-sky-200 text-sm">Welcome, {partner.name}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              className="p-2 rounded-xl bg-white/10 text-white hover:bg-white/20 transition"
              title="Refresh"
            >
              <FaSyncAlt className={loading ? "animate-spin" : ""} />
            </button>
            <button
              onClick={onLogout}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/10 text-white hover:bg-white/20 transition text-sm"
            >
              <FaSignOutAlt /> Log out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Stat cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            {
              icon: <FaUsers />,
              label: "Total Bookings",
              value: active.length,
              color: "text-sky-600",
            },
            {
              icon: <FaCalendarAlt />,
              label: "Pending",
              value: pending,
              color: "text-amber-600",
            },
            {
              icon: <FaCalendarAlt />,
              label: "Confirmed",
              value: confirmed,
              color: "text-emerald-600",
            },
            {
              icon: <FaEuroSign />,
              label: "Your Commission",
              value: `€${fmt(earned)}`,
              color: "text-purple-600",
            },
          ].map((s) => (
            <div
              key={s.label}
              className="bg-white rounded-2xl shadow-sm p-4 border border-slate-100"
            >
              <div className={`text-xl mb-1 ${s.color}`}>{s.icon}</div>
              <div className="text-2xl font-bold text-slate-800">{s.value}</div>
              <div className="text-xs text-slate-500 mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Revenue summary */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
          <h2 className="text-sm font-bold text-slate-600 uppercase tracking-wide mb-3">
            Revenue Summary
          </h2>
          <div className="flex flex-wrap gap-6">
            <div>
              <p className="text-xs text-slate-400">Total Booking Revenue</p>
              <p className="text-xl font-bold text-slate-800">
                €{fmt(totalRev)}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Your Commission (10%)</p>
              <p className="text-xl font-bold text-purple-600">
                €{fmt(earned)}
              </p>
            </div>
          </div>
        </div>

        {/* Bookings table */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="px-5 py-4 border-b flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide">
              Bookings
            </h2>
            <div className="flex gap-1.5 flex-wrap">
              {["all", "pending", "confirmed", "completed", "cancelled"].map(
                (s) => (
                  <button
                    key={s}
                    onClick={() => setFilter(s)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold capitalize transition ${
                      filter === s
                        ? "bg-sky-600 text-white"
                        : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                    }`}
                  >
                    {s}
                  </button>
                ),
              )}
            </div>
          </div>

          {loading ? (
            <div className="py-16 text-center text-slate-400 text-sm">
              Loading…
            </div>
          ) : visible.length === 0 ? (
            <div className="py-16 text-center text-slate-400 text-sm">
              No bookings found.
            </div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="sm:hidden divide-y divide-slate-100">
                {visible.map((b) => (
                  <div key={b.id} className="p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs text-sky-600 font-semibold">
                        {b.bookingReference || b.id}
                      </span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-semibold capitalize ${STATUS_COLORS[b.status] || ""}`}
                      >
                        {b.status}
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-slate-800">
                      {b.customerInfo?.name || "—"}
                    </p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                      <span>📅 {b.date || "—"}</span>
                      <span>🕐 {b.timeSlot || "—"}</span>
                      <span>👥 {b.numberOfGuests || "—"}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500">
                        Total:{" "}
                        <strong className="text-slate-700">
                          €{fmt(b.totalPrice)}
                        </strong>
                      </span>
                      <span className="text-purple-600 font-semibold">
                        Commission: €
                        {fmt(Number(b.totalPrice) * COMMISSION_RATE)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-400 tracking-wide">
                    <tr>
                      {[
                        "Reference",
                        "Guest",
                        "Service",
                        "Date",
                        "Guests",
                        "Total",
                        "Commission",
                        "Status",
                      ].map((h) => (
                        <th
                          key={h}
                          className="px-4 py-3 text-left font-semibold"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {visible.map((b) => (
                      <tr key={b.id} className="hover:bg-slate-50 transition">
                        <td className="px-4 py-3 font-mono text-xs text-sky-600">
                          {b.bookingReference || b.id.slice(0, 8)}
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-700">
                          {b.customerInfo?.name || "—"}
                        </td>
                        <td className="px-4 py-3 text-slate-500">
                          {b.serviceType || b.serviceId || "—"}
                        </td>
                        <td className="px-4 py-3 text-slate-500">
                          {b.date || "—"}
                        </td>
                        <td className="px-4 py-3 text-slate-500">
                          {b.numberOfGuests || "—"}
                        </td>
                        <td className="px-4 py-3 font-semibold text-slate-700">
                          €{fmt(b.totalPrice)}
                        </td>
                        <td className="px-4 py-3 font-semibold text-purple-600">
                          €{fmt(Number(b.totalPrice) * COMMISSION_RATE)}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full font-semibold capitalize ${STATUS_COLORS[b.status] || ""}`}
                          >
                            {b.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-xs text-slate-400 pb-4">
          Commission is calculated at 10% of confirmed & completed booking
          totals. Cancelled bookings are excluded.
        </p>
      </div>
    </div>
  );
}

/* ── Main export ────────────────────────────────────────────── */
export default function Partner() {
  const saved = (() => {
    try {
      return JSON.parse(sessionStorage.getItem(SESSION_KEY));
    } catch {
      return null;
    }
  })();
  const [partner, setPartner] = useState(saved);

  const handleLogout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setPartner(null);
  };

  if (!partner) return <LoginScreen onLogin={setPartner} />;
  return <Dashboard partner={partner} onLogout={handleLogout} />;
}
